import { useState, useEffect } from "react";
import { C, S, G, uid, today, fmtDate, isPast, calcGrade } from "../../utils/theme";
import { Modal, Inp, Txtarea, Sel, Badge, StatCard, ProgressBar, PageHdr, Empty, GlassCard, LevelBadge, FeesBadge } from "../../components/UI";
import { usersAPI, academicAPI, assessmentAPI, commAPI } from "../../utils/api";

// ── DASHBOARD ──────────────────────────────────────────────────────────────
export function AdminDashboard() {
  const [stats, setStats] = useState({ students:0, degrees:0, courses:0, exams:0, paid:0, partial:0, unpaid:0, qa:0 });
  const [recentStudents, setRecentStudents] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [studs, degrees, courses, exams, qa] = await Promise.all([
          usersAPI.getStudents(), academicAPI.getDegrees(), academicAPI.getCourses(),
          assessmentAPI.getExams(), commAPI.getQA(),
        ]);
        const students = studs.data || [];
        setRecentStudents(students.slice(0, 5));
        setStats({
          students: students.length,
          degrees: (degrees.data||[]).length,
          courses: (courses.data||[]).length,
          exams: (exams.data||[]).length,
          paid: students.filter(s => s.fees_status==="paid").length,
          partial: students.filter(s => s.fees_status==="partial").length,
          unpaid: students.filter(s => s.fees_status==="unpaid").length,
          qa: (qa.data||[]).length,
        });
      } catch(err) { console.error(err); }
    };
    load();
  }, []);

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h2 style={{ ...S.pageTitle, fontSize:26 }}>Welcome back 👋</h2>
        <p style={S.pageSub}>University portal overview — {new Date().toLocaleDateString("en-PK",{month:"long",year:"numeric"})}</p>
      </div>
      <div style={S.statsGrid}>
        <StatCard icon="👥" label="Total Students" value={stats.students} color="#2563EB" />
        <StatCard icon="🎓" label="Degrees" value={stats.degrees} color="#8B5CF6" />
        <StatCard icon="📚" label="Courses" value={stats.courses} color="#10B981" />
        <StatCard icon="📋" label="Exams" value={stats.exams} color="#F59E0B" />
        <StatCard icon="✅" label="Fees Paid" value={stats.paid} color="#10B981" sub={`${stats.unpaid} unpaid, ${stats.partial} partial`} />
        <StatCard icon="💬" label="Q&A Questions" value={stats.qa} color="#F43F5E" />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"16px 22px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}><span style={{ fontWeight:700, color:C.white }}>Fees Overview</span></div>
          <div style={{ padding:22 }}>
            {[
              { label:"Paid",    count:stats.paid,    color:"#10B981", pct:stats.students?Math.round(stats.paid/stats.students*100):0 },
              { label:"Partial", count:stats.partial, color:"#F59E0B", pct:stats.students?Math.round(stats.partial/stats.students*100):0 },
              { label:"Unpaid",  count:stats.unpaid,  color:"#F43F5E", pct:stats.students?Math.round(stats.unpaid/stats.students*100):0 },
            ].map(f => (
              <div key={f.label} style={{ marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:13, color:"#94A3B8" }}>{f.label}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:f.color }}>{f.count} students ({f.pct}%)</span>
                </div>
                <ProgressBar value={f.pct} color={f.color} />
              </div>
            ))}
          </div>
        </div>
        <div style={S.card}>
          <div style={{ padding:"16px 22px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}><span style={{ fontWeight:700, color:C.white }}>Recent Students</span></div>
          <div>
            {recentStudents.map(s => (
              <div key={s.id} style={{ padding:"11px 22px", borderBottom:"1px solid rgba(255,255,255,0.03)", display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:34, height:34, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, overflow:"hidden", flexShrink:0 }}>
                  {s.photo ? <img src={s.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.white }}>{s.name}</div>
                  <div style={{ fontSize:11, color:"#475569" }}>{s.student_id} — {s.degree_name} {s.program_name}</div>
                </div>
                <FeesBadge status={s.fees_status} />
              </div>
            ))}
            {recentStudents.length===0 && <div style={{ padding:24, textAlign:"center", color:"#475569", fontSize:13 }}>No students yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DEGREES & PROGRAMS ─────────────────────────────────────────────────────
export function AdminDegrees() {
  const [tab, setTab] = useState("degrees");
  const [degrees, setDegrees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name:"", short_name:"", icon:"🎓", duration:"", color:"#2563EB" });
  const [progModal, setProgModal] = useState(false);
  const [editProg, setEditProg] = useState(null);
  const [progForm, setProgForm] = useState({ name:"", code:"", degree_id:"" });

  const load = async () => {
    try {
      const [d, p] = await Promise.all([academicAPI.getDegrees(), academicAPI.getPrograms()]);
      setDegrees(d.data||[]); setPrograms(p.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const saveDeg = async () => {
    if (!form.name) return;
    try {
      if (editItem) await academicAPI.updateDegree(editItem.id, form);
      else await academicAPI.createDegree(form);
      setModal(false); setEditItem(null); load();
    } catch(e) { alert(e.message); }
  };
  const saveProg = async () => {
    if (!progForm.name || !progForm.degree_id) return;
    try {
      if (editProg) await academicAPI.updateProgram(editProg.id, progForm);
      else await academicAPI.createProgram(progForm);
      setProgModal(false); setEditProg(null); load();
    } catch(e) { alert(e.message); }
  };
  const delDeg = async (id) => {
    if (!window.confirm("Delete degree?")) return;
    try { await academicAPI.deleteDegree(id); load(); } catch(e) { alert(e.message); }
  };
  const delProg = async (id) => {
    if (!window.confirm("Delete program?")) return;
    try { await academicAPI.deleteProgram(id); load(); } catch(e) { alert(e.message); }
  };

  return (
    <div>
      <PageHdr title="Degrees & Programs" action={
        <button style={S.btn("primary")} onClick={() => {
          setEditItem(null);
          if (tab==="degrees") { setForm({ name:"", short_name:"", icon:"🎓", duration:"", color:"#2563EB" }); setModal(true); }
          else { setProgForm({ name:"", code:"", degree_id:"" }); setProgModal(true); }
        }}>➕ Add {tab==="degrees"?"Degree":"Program"}</button>
      } />
      <div style={S.tabRow}>
        <button style={S.tab(tab==="degrees")} onClick={()=>setTab("degrees")}>🎓 Degrees ({degrees.length})</button>
        <button style={S.tab(tab==="programs")} onClick={()=>setTab("programs")}>📋 Programs ({programs.length})</button>
      </div>
      {tab==="degrees" && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:16 }}>
          {degrees.map(d => {
            const progs = programs.filter(p => p.degree_id===d.id);
            return (
              <GlassCard key={d.id} style={{ padding:22, borderTop:`3px solid ${d.color}` }}>
                <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:14 }}>
                  <div style={{ width:48, height:48, background:`${d.color}22`, border:`2px solid ${d.color}44`, borderRadius:14, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>{d.icon}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, color:C.white }}>{d.name}</div>
                    <div style={{ fontSize:12, color:"#64748B" }}>{d.short_name} • {d.duration}</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, marginBottom:14 }}>
                  <Badge type="info">{progs.length} programs</Badge>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(d); setForm({ name:d.name, short_name:d.short_name, icon:d.icon, duration:d.duration, color:d.color }); setModal(true); }}>✏️</button>
                  <button style={S.btn("danger","sm")} onClick={()=>delDeg(d.id)}>🗑️</button>
                </div>
              </GlassCard>
            );
          })}
          {degrees.length===0 && <Empty icon="🎓" text="No degrees yet" />}
        </div>
      )}
      {tab==="programs" && (
        <div style={S.card}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Program</th><th style={S.th}>Code</th><th style={S.th}>Degree</th><th style={S.th}>Actions</th></tr></thead>
            <tbody>
              {programs.map(p => (
                <tr key={p.id}>
                  <td style={S.td}><strong style={{ color:C.white }}>{p.name}</strong></td>
                  <td style={S.td}><Badge type="info">{p.code}</Badge></td>
                  <td style={S.td}><span style={{ fontSize:13, color:"#94A3B8" }}>{p.degree_name||"—"}</span></td>
                  <td style={S.td}>
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={S.btn("glass","sm")} onClick={()=>{ setEditProg(p); setProgForm({ name:p.name, code:p.code, degree_id:p.degree_id }); setProgModal(true); }}>✏️</button>
                      <button style={S.btn("danger","sm")} onClick={()=>delProg(p.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {programs.length===0 && <tr><td colSpan={4} style={{ ...S.td, textAlign:"center", color:"#475569", padding:32 }}>No programs yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit Degree":"Add Degree"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveDeg}>Save</button></>}>
        <Inp label="Name *" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
          <Inp label="Short Name" placeholder="BS" value={form.short_name} onChange={e=>setForm({...form,short_name:e.target.value})} />
          <Inp label="Icon" placeholder="🎓" value={form.icon} onChange={e=>setForm({...form,icon:e.target.value})} />
          <Inp label="Duration" placeholder="4 years" value={form.duration} onChange={e=>setForm({...form,duration:e.target.value})} />
        </div>
        <div style={S.formGroup}><label style={S.label}>Color</label><input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} style={{ width:"100%", height:44, borderRadius:8, border:"none", cursor:"pointer", background:"none" }} /></div>
      </Modal>
      <Modal open={progModal} onClose={()=>setProgModal(false)} title={editProg?"Edit Program":"Add Program"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setProgModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveProg}>Save</button></>}>
        <Sel label="Degree *" value={progForm.degree_id} onChange={e=>setProgForm({...progForm,degree_id:e.target.value})}>
          <option value="">Select Degree</option>
          {degrees.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
        </Sel>
        <Inp label="Program Name *" placeholder="Computer Science" value={progForm.name} onChange={e=>setProgForm({...progForm,name:e.target.value})} />
        <Inp label="Code" placeholder="BS-CS" value={progForm.code} onChange={e=>setProgForm({...progForm,code:e.target.value})} />
      </Modal>
    </div>
  );
}

// ── COURSES ────────────────────────────────────────────────────────────────
export function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name:"", code:"", program_id:"", level:"general", credits:3, description:"", outline:"" });

  const load = async () => {
    try {
      const [c, p, d] = await Promise.all([academicAPI.getCourses(), academicAPI.getPrograms(), academicAPI.getDegrees()]);
      setCourses(c.data||[]); setPrograms(p.data||[]); setDegrees(d.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.program_id) return;
    try {
      if (editItem) await academicAPI.updateCourse(editItem.id, form);
      else await academicAPI.createCourse(form);
      setModal(false); setEditItem(null); load();
    } catch(e) { alert(e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await academicAPI.deleteCourse(id); load(); } catch(e) { alert(e.message); }
  };

  const filtered = filter==="all" ? courses : courses.filter(c=>c.level===filter);
  const levelColor = { general:"#3B82F6", core:"#8B5CF6", specialist:"#F59E0B" };

  return (
    <div>
      <PageHdr title="Course Management" sub={`${courses.length} total courses`}
        action={<button style={S.btn("primary")} onClick={()=>{ setEditItem(null); setForm({ name:"", code:"", program_id:"", level:"general", credits:3, description:"", outline:"" }); setModal(true); }}>➕ Add Course</button>} />
      <div style={{ display:"flex", gap:8, marginBottom:22, flexWrap:"wrap" }}>
        {["all","general","core","specialist"].map(l=>(
          <button key={l} style={S.btn(filter===l?"primary":"glass","sm")} onClick={()=>setFilter(l)}>
            {l==="all"?"All":l==="general"?"🔵 General":l==="core"?"🟣 Core":"🟡 Specialist"}
          </button>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
        {filtered.map(c => (
          <GlassCard key={c.id} style={{ borderLeft:`3px solid ${levelColor[c.level]||"#334155"}` }}>
            <div style={{ padding:"18px 20px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:C.white, marginBottom:4 }}>{c.name}</div>
                  <Badge type="info">{c.code}</Badge>
                </div>
                <LevelBadge level={c.level} />
              </div>
              <div style={{ fontSize:12, color:"#64748B", marginBottom:8 }}>{c.short_name} → {c.program_name}</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
                <Badge type="gray">⭐ {c.credits} cr</Badge>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(c); setForm({ name:c.name, code:c.code, program_id:c.program_id, level:c.level, credits:c.credits, description:c.description||"", outline:c.outline||"" }); setModal(true); }}>✏️ Edit</button>
                <button style={S.btn("danger","sm")} onClick={()=>del(c.id)}>🗑️</button>
              </div>
            </div>
          </GlassCard>
        ))}
        {filtered.length===0 && <Empty icon="📚" text="No courses" />}
      </div>
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit Course":"Add Course"} w={620}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={save}>Save</button></>}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <Inp label="Name *" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          <Inp label="Code" placeholder="CS-101" value={form.code} onChange={e=>setForm({...form,code:e.target.value})} />
        </div>
        <Sel label="Program *" value={form.program_id} onChange={e=>setForm({...form,program_id:e.target.value})}>
          <option value="">Select Program</option>
          {programs.map(p=>{ const d=degrees.find(x=>x.id===p.degree_id); return <option key={p.id} value={p.id}>{d?.short_name} — {p.name}</option>; })}
        </Sel>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <Sel label="Level *" value={form.level} onChange={e=>setForm({...form,level:e.target.value})}>
            <option value="general">🔵 General</option>
            <option value="core">🟣 Core</option>
            <option value="specialist">🟡 Specialist</option>
          </Sel>
          <Inp label="Credits" type="number" min={1} max={6} value={form.credits} onChange={e=>setForm({...form,credits:parseInt(e.target.value)||3})} />
        </div>
        <Txtarea label="Description" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} />
        <Txtarea label="Course Outline" placeholder={"Week 1: Topic...\nWeek 2: Topic..."} rows={6} value={form.outline} onChange={e=>setForm({...form,outline:e.target.value})} />
      </Modal>
    </div>
  );
}

// ── MODULES & LECTURES ─────────────────────────────────────────────────────
export function AdminModules() {
  const [tab, setTab] = useState("modules");
  const [modules, setModules] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modModal, setModModal] = useState(false);
  const [editMod, setEditMod] = useState(null);
  const [modForm, setModForm] = useState({ name:"", course_id:"", order:1 });
  const [lecModal, setLecModal] = useState(false);
  const [editLec, setEditLec] = useState(null);
  const [lecForm, setLecForm] = useState({ title:"", module_id:"", description:"", type:"youtube", url:"" });
  const [videoFile, setVideoFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  const load = async () => {
    try {
      const [m, l, c] = await Promise.all([academicAPI.getModules(), academicAPI.getLectures(), academicAPI.getCourses()]);
      setModules(m.data||[]); setLectures(l.data||[]); setCourses(c.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const saveMod = async () => {
    if (!modForm.name || !modForm.course_id) return;
    try {
      if (editMod) await academicAPI.updateModule(editMod.id, modForm);
      else await academicAPI.createModule(modForm);
      setModModal(false); setEditMod(null); load();
    } catch(e) { alert(e.message); }
  };

  const saveLec = async () => {
    if (!lecForm.title || !lecForm.module_id) return;
    try {
      const fd = new FormData();
      Object.keys(lecForm).forEach(k => fd.append(k, lecForm[k]));
      if (videoFile) fd.append("video", videoFile);
      if (pdfFile) fd.append("pdf", pdfFile);
      if (editLec) await academicAPI.updateLecture(editLec.id, fd);
      else await academicAPI.createLecture(fd);
      setLecModal(false); setEditLec(null); setVideoFile(null); setPdfFile(null); load();
    } catch(e) { alert(e.message); }
  };

  const delMod = async (id) => { if(!window.confirm("Delete?")) return; try { await academicAPI.deleteModule(id); load(); } catch(e) { alert(e.message); } };
  const delLec = async (id) => { if(!window.confirm("Delete?")) return; try { await academicAPI.deleteLecture(id); load(); } catch(e) { alert(e.message); } };

  return (
    <div>
      <PageHdr title="Modules & Lectures" action={
        <button style={S.btn("primary")} onClick={()=>{
          if(tab==="modules"){ setEditMod(null); setModForm({ name:"", course_id:"", order:1 }); setModModal(true); }
          else { setEditLec(null); setLecForm({ title:"", module_id:"", description:"", type:"youtube", url:"" }); setVideoFile(null); setPdfFile(null); setLecModal(true); }
        }}>➕ Add {tab==="modules"?"Module":"Lecture"}</button>
      } />
      <div style={S.tabRow}>
        <button style={S.tab(tab==="modules")} onClick={()=>setTab("modules")}>📦 Modules ({modules.length})</button>
        <button style={S.tab(tab==="lectures")} onClick={()=>setTab("lectures")}>🎬 Lectures ({lectures.length})</button>
      </div>
      {tab==="modules" && (
        <div style={S.card}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Module</th><th style={S.th}>Course</th><th style={S.th}>Level</th><th style={S.th}>Order</th><th style={S.th}>Actions</th></tr></thead>
            <tbody>
              {modules.map(m=>(
                <tr key={m.id}>
                  <td style={S.td}><strong style={{ color:C.white }}>{m.name}</strong></td>
                  <td style={S.td}><span style={{ fontSize:13, color:"#94A3B8" }}>{m.course_name||"—"}</span></td>
                  <td style={S.td}>{m.course_level && <LevelBadge level={m.course_level} />}</td>
                  <td style={S.td}><Badge type="gray">#{m.order}</Badge></td>
                  <td style={S.td}>
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={S.btn("glass","sm")} onClick={()=>{ setEditMod(m); setModForm({ name:m.name, course_id:m.course_id, order:m.order }); setModModal(true); }}>✏️</button>
                      <button style={S.btn("danger","sm")} onClick={()=>delMod(m.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {modules.length===0 && <tr><td colSpan={5} style={{ ...S.td, textAlign:"center", color:"#475569", padding:32 }}>No modules yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab==="lectures" && (
        <div style={S.card}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Title</th><th style={S.th}>Module</th><th style={S.th}>Type</th><th style={S.th}>Files</th><th style={S.th}>Actions</th></tr></thead>
            <tbody>
              {lectures.map(l=>(
                <tr key={l.id}>
                  <td style={S.td}><strong style={{ color:C.white }}>{l.title}</strong></td>
                  <td style={S.td}><span style={{ fontSize:13, color:"#94A3B8" }}>{l.module_name||"—"}</span></td>
                  <td style={S.td}><Badge type={l.type==="youtube"?"danger":"success"}>{l.type==="youtube"?"▶ YouTube":"📁 Upload"}</Badge></td>
                  <td style={S.td}>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {l.video_url && <Badge type="success">🎬 Video</Badge>}
                      {l.pdf_url && <Badge type="info">📄 PDF</Badge>}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={S.btn("glass","sm")} onClick={()=>{ setEditLec(l); setLecForm({ title:l.title, module_id:l.module_id, description:l.description||"", type:l.type, url:l.url||"" }); setLecModal(true); }}>✏️</button>
                      <button style={S.btn("danger","sm")} onClick={()=>delLec(l.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {lectures.length===0 && <tr><td colSpan={5} style={{ ...S.td, textAlign:"center", color:"#475569", padding:32 }}>No lectures yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={modModal} onClose={()=>setModModal(false)} title={editMod?"Edit Module":"Add Module"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveMod}>Save</button></>}>
        <Inp label="Module Name *" value={modForm.name} onChange={e=>setModForm({...modForm,name:e.target.value})} />
        <Sel label="Course *" value={modForm.course_id} onChange={e=>setModForm({...modForm,course_id:e.target.value})}>
          <option value="">Select Course</option>
          {courses.map(c=><option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
        </Sel>
        <Inp label="Order" type="number" min={1} value={modForm.order} onChange={e=>setModForm({...modForm,order:parseInt(e.target.value)||1})} />
      </Modal>
      <Modal open={lecModal} onClose={()=>setLecModal(false)} title={editLec?"Edit Lecture":"Add Lecture"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setLecModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveLec}>Save</button></>}>
        <Inp label="Title *" value={lecForm.title} onChange={e=>setLecForm({...lecForm,title:e.target.value})} />
        <Sel label="Module *" value={lecForm.module_id} onChange={e=>setLecForm({...lecForm,module_id:e.target.value})}>
          <option value="">Select Module</option>
          {modules.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </Sel>
        <Txtarea label="Description" rows={2} value={lecForm.description} onChange={e=>setLecForm({...lecForm,description:e.target.value})} />
        <div style={S.formGroup}>
          <label style={S.label}>Type</label>
          <div style={{ display:"flex", gap:10 }}>
            {["youtube","upload"].map(t=>(
              <label key={t} style={{ flex:1, padding:"11px 16px", border:`2px solid ${lecForm.type===t?"#3B82F6":"rgba(255,255,255,0.08)"}`, borderRadius:10, cursor:"pointer", textAlign:"center", color:lecForm.type===t?"#3B82F6":"#64748B", background:lecForm.type===t?"rgba(59,130,246,0.08)":"transparent", fontSize:13, fontWeight:lecForm.type===t?700:400 }}>
                <input type="radio" checked={lecForm.type===t} onChange={()=>setLecForm({...lecForm,type:t})} style={{ display:"none" }} />
                {t==="youtube"?"▶ YouTube":"📁 Upload"}
              </label>
            ))}
          </div>
        </div>
        {lecForm.type==="youtube"
          ? <Inp label="YouTube URL" placeholder="https://youtube.com/watch?v=..." value={lecForm.url} onChange={e=>setLecForm({...lecForm,url:e.target.value})} />
          : <div>
              <div style={S.formGroup}>
                <label style={S.label}>Upload Video (optional)</label>
                <input type="file" accept="video/*" onChange={e=>setVideoFile(e.target.files[0])} style={{ color:"#94A3B8", fontSize:13 }} />
                {videoFile && <div style={{ fontSize:12, color:"#10B981", marginTop:6 }}>✅ {videoFile.name}</div>}
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Upload PDF (optional)</label>
                <input type="file" accept=".pdf" onChange={e=>setPdfFile(e.target.files[0])} style={{ color:"#94A3B8", fontSize:13 }} />
                {pdfFile && <div style={{ fontSize:12, color:"#3B82F6", marginTop:6 }}>📄 {pdfFile.name}</div>}
              </div>
            </div>
        }
      </Modal>
    </div>
  );
}

// ── STUDENTS ────────────────────────────────────────────────────────────────
export function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name:"", email:"", password:"", degree_id:"", program_id:"", enroll_year:new Date().getFullYear() });
  const [photoFile, setPhotoFile] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [s, d, p] = await Promise.all([usersAPI.getStudents(), academicAPI.getDegrees(), academicAPI.getPrograms()]);
      setStudents(s.data||[]); setDegrees(d.data||[]); setPrograms(p.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    (s.student_id||"").includes(search)
  );

  const save = async () => {
    if (!form.name || !form.email || (!editItem && !form.password)) return alert("Fill required fields.");
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k => { if (form[k]) fd.append(k, form[k]); });
      if (photoFile) fd.append("photo", photoFile);
      if (editItem) await usersAPI.updateStudent(editItem.id, fd);
      else await usersAPI.createStudent(fd);
      setModal(false); setEditItem(null); setPhotoFile(null); load();
    } catch(e) { alert(e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete student?")) return;
    try { await usersAPI.deleteStudent(id); load(); } catch(e) { alert(e.message); }
  };

  return (
    <div>
      <PageHdr title="Student Management" sub={`${filtered.length} students`}
        action={<button style={S.btn("primary")} onClick={()=>{ setEditItem(null); setForm({ name:"", email:"", password:"", degree_id:"", program_id:"", enroll_year:new Date().getFullYear() }); setPhotoFile(null); setModal(true); }}>➕ Add Student</button>} />
      <div style={S.card}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <input style={{ ...S.input, maxWidth:340 }} placeholder="🔍 Search name, email, ID..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Student</th><th style={S.th}>ID</th><th style={S.th}>Degree / Program</th><th style={S.th}>Fees</th><th style={S.th}>Actions</th></tr></thead>
          <tbody>
            {filtered.map(s=>(
              <tr key={s.id}>
                <td style={S.td}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, overflow:"hidden", flexShrink:0 }}>
                      {s.photo ? <img src={s.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
                    </div>
                    <div><div style={{ fontWeight:600, color:C.white, fontSize:13 }}>{s.name}</div><div style={{ fontSize:11, color:"#475569" }}>{s.email}</div></div>
                  </div>
                </td>
                <td style={S.td}><Badge type="info">{s.student_id}</Badge></td>
                <td style={S.td}><span style={{ fontSize:13, color:"#94A3B8" }}>{s.short_name||"—"} / {s.program_name||"—"}</span></td>
                <td style={S.td}><FeesBadge status={s.fees_status} /></td>
                <td style={S.td}>
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(s); setForm({ name:s.name, email:s.email, password:"", degree_id:s.degree_id||"", program_id:s.program_id||"", enroll_year:s.enroll_year }); setPhotoFile(null); setModal(true); }}>✏️</button>
                    <button style={S.btn("danger","sm")} onClick={()=>del(s.id)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan={5} style={{ ...S.td, textAlign:"center", color:"#475569", padding:36 }}>No students</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit Student":"Add Student"} w={600}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={save}>Save</button></>}>
        <div style={S.formGroup}>
          <label style={S.label}>Photo</label>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, overflow:"hidden", border:"2px solid rgba(255,255,255,0.1)" }}>
              {photoFile ? <img src={URL.createObjectURL(photoFile)} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : editItem?.photo ? <img src={editItem.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
            </div>
            <input type="file" accept="image/*" onChange={e=>setPhotoFile(e.target.files[0])} style={{ color:"#94A3B8", fontSize:12 }} />
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <Inp label="Full Name *" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          <Inp label="Email *" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
          <Inp label={editItem?"New Password (leave blank to keep)":"Password *"} type="text" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} />
          <Inp label="Enrollment Year" type="number" value={form.enroll_year} onChange={e=>setForm({...form,enroll_year:parseInt(e.target.value)})} />
          <Sel label="Degree" value={form.degree_id} onChange={e=>setForm({...form,degree_id:e.target.value,program_id:""})}>
            <option value="">Select Degree</option>
            {degrees.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
          </Sel>
          <Sel label="Program" value={form.program_id} onChange={e=>setForm({...form,program_id:e.target.value})}>
            <option value="">Select Program</option>
            {programs.filter(p=>!form.degree_id||p.degree_id===form.degree_id).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </Sel>
        </div>
      </Modal>
    </div>
  );
}

// ── FEES ────────────────────────────────────────────────────────────────────
export function AdminFees() {
  const [students, setStudents] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ fees_status:"paid", fees_amount:0, fees_paid:0, fees_due:"", fees_note:"" });

  const load = async () => { try { const s = await usersAPI.getStudents(); setStudents(s.data||[]); } catch(e) { console.error(e); } };
  useEffect(() => { load(); }, []);

  const openFees = (s) => { setModal(s); setForm({ fees_status:s.fees_status||"unpaid", fees_amount:s.fees_amount||0, fees_paid:s.fees_paid||0, fees_due:s.fees_due?s.fees_due.split("T")[0]:"", fees_note:s.fees_note||"" }); };
  const saveFees = async () => {
    try { await usersAPI.updateFees(modal.id, form); setModal(null); load(); } catch(e) { alert(e.message); }
  };

  const total = students.reduce((a,s)=>a+(parseFloat(s.fees_amount)||0),0);
  const collected = students.reduce((a,s)=>a+(parseFloat(s.fees_paid)||0),0);

  return (
    <div>
      <PageHdr title="Fees Management" sub="Track and update student fees" />
      <div style={S.statsGrid}>
        <StatCard icon="💰" label="Total Revenue" value={`Rs. ${collected.toLocaleString()}`} color="#10B981" />
        <StatCard icon="⏳" label="Pending" value={`Rs. ${(total-collected).toLocaleString()}`} color="#F43F5E" />
        <StatCard icon="✅" label="Paid" value={students.filter(s=>s.fees_status==="paid").length} color="#10B981" />
        <StatCard icon="⚠️" label="Partial/Unpaid" value={students.filter(s=>s.fees_status!=="paid").length} color="#F59E0B" />
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Student</th><th style={S.th}>Total</th><th style={S.th}>Paid</th><th style={S.th}>Remaining</th><th style={S.th}>Status</th><th style={S.th}>Due</th><th style={S.th}>Action</th></tr></thead>
          <tbody>
            {students.map(s=>{
              const rem = (parseFloat(s.fees_amount)||0)-(parseFloat(s.fees_paid)||0);
              const overdue = s.fees_due && new Date(s.fees_due) < new Date() && s.fees_status!=="paid";
              return (
                <tr key={s.id}>
                  <td style={S.td}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:"50%", background:G.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, overflow:"hidden" }}>
                        {s.photo?<img src={s.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />:"👤"}
                      </div>
                      <div>
                        <div style={{ fontWeight:600, color:C.white, fontSize:13 }}>{s.name}</div>
                        <div style={{ fontSize:11, color:"#475569" }}>{s.student_id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={S.td}><span style={{ color:"#CBD5E1" }}>Rs. {(parseFloat(s.fees_amount)||0).toLocaleString()}</span></td>
                  <td style={S.td}><span style={{ color:"#10B981" }}>Rs. {(parseFloat(s.fees_paid)||0).toLocaleString()}</span></td>
                  <td style={S.td}><span style={{ color:rem>0?"#F43F5E":"#10B981" }}>Rs. {rem.toLocaleString()}</span></td>
                  <td style={S.td}><FeesBadge status={s.fees_status} /></td>
                  <td style={S.td}><span style={{ color:overdue?"#F43F5E":"#64748B", fontSize:13 }}>{s.fees_due?new Date(s.fees_due).toLocaleDateString("en-PK"):"—"}{overdue&&" ⚠️"}</span></td>
                  <td style={S.td}><button style={S.btn("primary","sm")} onClick={()=>openFees(s)}>💰 Update</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal open={!!modal} onClose={()=>setModal(null)} title={`Fees — ${modal?.name}`}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={saveFees}>Save & Notify</button></>}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
          {["paid","partial","unpaid"].map(st=>(
            <label key={st} style={{ padding:"14px 8px", border:`2px solid ${form.fees_status===st?(st==="paid"?"#10B981":st==="partial"?"#F59E0B":"#F43F5E"):"rgba(255,255,255,0.08)"}`, borderRadius:12, cursor:"pointer", textAlign:"center", background:form.fees_status===st?"rgba(255,255,255,0.05)":"transparent" }}>
              <input type="radio" checked={form.fees_status===st} onChange={()=>setForm({...form,fees_status:st})} style={{ display:"none" }} />
              <div style={{ fontSize:24 }}>{st==="paid"?"✅":st==="partial"?"⚠️":"❌"}</div>
              <div style={{ fontSize:12, fontWeight:700, color:st==="paid"?"#10B981":st==="partial"?"#F59E0B":"#F43F5E", marginTop:6, textTransform:"capitalize" }}>{st}</div>
            </label>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <Inp label="Total Fees (Rs.)" type="number" value={form.fees_amount} onChange={e=>setForm({...form,fees_amount:parseInt(e.target.value)||0})} />
          <Inp label="Amount Paid (Rs.)" type="number" value={form.fees_paid} onChange={e=>setForm({...form,fees_paid:parseInt(e.target.value)||0})} />
        </div>
        <Inp label="Due Date" type="date" value={form.fees_due} onChange={e=>setForm({...form,fees_due:e.target.value})} />
        <Txtarea label="Note" rows={2} placeholder="e.g. Scholarship, installment..." value={form.fees_note} onChange={e=>setForm({...form,fees_note:e.target.value})} />
        <div style={S.alert("info")}>💡 Student gets instant notification on save.</div>
      </Modal>
    </div>
  );
}

// ── ASSIGNMENTS ────────────────────────────────────────────────────────────
export function AdminAssignments() {
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title:"", course_id:"", module_id:"", description:"", due_date:"" });
  const [pdfFile, setPdfFile] = useState(null);
  const [viewSubs, setViewSubs] = useState(null);
  const [subs, setSubs] = useState([]);
  const [gradeModal, setGradeModal] = useState(null);
  const [gradeForm, setGradeForm] = useState({ marks:"", feedback:"" });

  const load = async () => {
    try {
      const [a, c, m] = await Promise.all([assessmentAPI.getAssignments(), academicAPI.getCourses(), academicAPI.getModules()]);
      setAssignments(a.data||[]); setCourses(c.data||[]); setModules(m.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const loadSubs = async (assignId) => {
    try { const s = await assessmentAPI.getSubmissions(assignId); setSubs(s.data||[]); } catch(e) { console.error(e); }
  };

  const save = async () => {
    if (!form.title || !form.course_id) return;
    try {
      const fd = new FormData();
      Object.keys(form).forEach(k=>{ if(form[k]) fd.append(k,form[k]); });
      if (pdfFile) fd.append("pdf", pdfFile);
      if (editItem) await assessmentAPI.updateAssignment(editItem.id, fd);
      else await assessmentAPI.createAssignment(fd);
      setModal(false); setEditItem(null); setPdfFile(null); load();
    } catch(e) { alert(e.message); }
  };
  const saveGrade = async () => {
    try { await assessmentAPI.gradeSubmission(gradeModal.id, gradeForm); setGradeModal(null); loadSubs(viewSubs.id); } catch(e) { alert(e.message); }
  };
  const del = async (id) => { if(!window.confirm("Delete?")) return; try { await assessmentAPI.deleteAssignment(id); load(); } catch(e) { alert(e.message); } };

  return (
    <div>
      <PageHdr title="Assignments" action={<button style={S.btn("primary")} onClick={()=>{ setEditItem(null); setForm({ title:"", course_id:"", module_id:"", description:"", due_date:"" }); setPdfFile(null); setModal(true); }}>➕ Add</button>} />
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Title</th><th style={S.th}>Course</th><th style={S.th}>Level</th><th style={S.th}>Due</th><th style={S.th}>Subs</th><th style={S.th}>Actions</th></tr></thead>
          <tbody>
            {assignments.map(a=>{
              const overdue = a.due_date && new Date(a.due_date) < new Date();
              return (
                <tr key={a.id}>
                  <td style={S.td}><strong style={{ color:C.white }}>{a.title}</strong>{a.pdf_name&&<div style={{ fontSize:11, color:"#10B981" }}>📄 {a.pdf_name}</div>}</td>
                  <td style={S.td}><span style={{ fontSize:13, color:"#94A3B8" }}>{a.course_name||"—"}</span></td>
                  <td style={S.td}>{a.course_level&&<LevelBadge level={a.course_level} />}</td>
                  <td style={S.td}><span style={{ color:overdue?"#F43F5E":"#64748B", fontSize:13 }}>{a.due_date?new Date(a.due_date).toLocaleDateString("en-PK"):"—"}</span></td>
                  <td style={S.td}><button style={{ ...S.btn("ghost","sm"), padding:0 }} onClick={()=>{ setViewSubs(a); loadSubs(a.id); }}><Badge type="info">View</Badge></button></td>
                  <td style={S.td}><div style={{ display:"flex", gap:8 }}><button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(a); setForm({ title:a.title, course_id:a.course_id, module_id:a.module_id||"", description:a.description||"", due_date:a.due_date?a.due_date.split("T")[0]:"" }); setModal(true); }}>✏️</button><button style={S.btn("danger","sm")} onClick={()=>del(a.id)}>🗑️</button></div></td>
                </tr>
              );
            })}
            {assignments.length===0&&<tr><td colSpan={6} style={{ ...S.td, textAlign:"center", color:"#475569", padding:32 }}>No assignments</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={!!viewSubs} onClose={()=>setViewSubs(null)} title={`Submissions — ${viewSubs?.title}`} w={640}>
        {subs.map(sub=>(
          <div key={sub.id} style={{ padding:"14px 0", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontWeight:700, color:C.white }}>{sub.student_name}</div>
              <div style={{ fontSize:12, color:"#475569" }}>{sub.student_id} • {sub.submitted_at?new Date(sub.submitted_at).toLocaleDateString("en-PK"):"—"}</div>
              {sub.pdf_name&&<div style={{ fontSize:12, color:"#10B981", marginTop:3 }}>📄 {sub.pdf_name}</div>}
            </div>
            <div style={{ textAlign:"right" }}>
              <Badge type={sub.status==="graded"?"success":sub.status==="auto-submitted"?"warning":"info"}>{sub.status}</Badge>
              {sub.marks!=null&&<div style={{ fontSize:22, fontWeight:800, color:"#10B981", marginTop:4 }}>{sub.marks}%</div>}
              {sub.status!=="graded"&&<button style={{ ...S.btn("primary","sm"), marginTop:8 }} onClick={()=>{ setGradeModal(sub); setGradeForm({ marks:"", feedback:"" }); }}>Grade</button>}
            </div>
          </div>
        ))}
        {subs.length===0&&<div style={{ textAlign:"center", color:"#475569", padding:24 }}>No submissions yet</div>}
      </Modal>
      <Modal open={!!gradeModal} onClose={()=>setGradeModal(null)} title="Grade Submission"
        footer={<><button style={S.btn("ghost")} onClick={()=>setGradeModal(null)}>Cancel</button><button style={S.btn("primary")} onClick={saveGrade}>Save</button></>}>
        <Inp label="Marks (0-100)" type="number" min={0} max={100} value={gradeForm.marks} onChange={e=>setGradeForm({...gradeForm,marks:e.target.value})} />
        <Txtarea label="Feedback" rows={3} value={gradeForm.feedback} onChange={e=>setGradeForm({...gradeForm,feedback:e.target.value})} />
      </Modal>
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit":"Add Assignment"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={save}>Save</button></>}>
        <Inp label="Title *" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} />
        <Sel label="Course *" value={form.course_id} onChange={e=>setForm({...form,course_id:e.target.value})}>
          <option value="">Select</option>
          {courses.map(c=><option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
        </Sel>
        <Sel label="Module" value={form.module_id} onChange={e=>setForm({...form,module_id:e.target.value})}>
          <option value="">Optional</option>
          {modules.filter(m=>!form.course_id||m.course_id===form.course_id).map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
        </Sel>
        <Txtarea label="Instructions" rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} />
        <Inp label="Due Date" type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} />
        <div style={S.formGroup}><label style={S.label}>Upload PDF</label><input type="file" accept=".pdf" onChange={e=>setPdfFile(e.target.files[0])} style={{ color:"#94A3B8", fontSize:13 }} />{pdfFile&&<div style={{ fontSize:12, color:"#10B981", marginTop:6 }}>✅ {pdfFile.name}</div>}</div>
      </Modal>
    </div>
  );
}

// ── EXAMS ──────────────────────────────────────────────────────────────────
export function AdminExams() {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title:"", course_id:"", duration:60 });
  const [questions, setQuestions] = useState([]);
  const [qModal, setQModal] = useState(false);
  const [editQIdx, setEditQIdx] = useState(null);
  const [qForm, setQForm] = useState({ question:"", options:["","","",""], correct:0 });
  const [viewRes, setViewRes] = useState(null);
  const [results, setResults] = useState([]);

  const load = async () => {
    try {
      const [e, c] = await Promise.all([assessmentAPI.getExams(), academicAPI.getCourses()]);
      setExams(e.data||[]); setCourses(c.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const loadResults = async (examId) => {
    try { const r = await assessmentAPI.getResults(examId); setResults(r.data||[]); } catch(e) { console.error(e); }
  };

  const saveExam = async () => {
    if (!form.title || !form.course_id || questions.length===0) { alert("Add title, course and questions."); return; }
    try {
      const payload = { ...form, questions: questions.map(q=>({ question:q.question, options:q.options, correct:q.correct })) };
      if (editItem) await assessmentAPI.updateExam(editItem.id, payload);
      else await assessmentAPI.createExam(payload);
      setModal(false); load();
    } catch(e) { alert(e.message); }
  };
  const saveQ = () => {
    if (!qForm.question || qForm.options.some(o=>!o.trim())) { alert("Fill all fields."); return; }
    const q = { ...qForm };
    if (editQIdx!==null) { const qs=[...questions]; qs[editQIdx]=q; setQuestions(qs); } else setQuestions([...questions,q]);
    setQModal(false);
  };
  const del = async (id) => { if(!window.confirm("Delete?")) return; try { await assessmentAPI.deleteExam(id); load(); } catch(e) { alert(e.message); } };
  const levelColor = { general:"#3B82F6", core:"#8B5CF6", specialist:"#F59E0B" };

  return (
    <div>
      <PageHdr title="Exams & MCQs" sub="Min 60% guaranteed for all students"
        action={<button style={S.btn("primary")} onClick={()=>{ setEditItem(null); setForm({ title:"", course_id:"", duration:60 }); setQuestions([]); setModal(true); }}>➕ Create Exam</button>} />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))", gap:16 }}>
        {exams.map(e=>{
          const course = courses.find(c=>c.id===e.course_id);
          return (
            <GlassCard key={e.id} style={{ borderTop:`2px solid ${levelColor[e.course_level]||"#334155"}` }}>
              <div style={{ padding:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                  <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:C.white, flex:1, paddingRight:8 }}>{e.title}</h3>
                  <Badge type="info">{(e.questions||[]).length}Q</Badge>
                </div>
                {e.course_level && <div style={{ marginBottom:10 }}><LevelBadge level={e.course_level} /></div>}
                <div style={{ fontSize:12, color:"#64748B", marginBottom:12 }}>📚 {e.course_name||"—"} | ⏱ {e.duration}min</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(e); setForm({ title:e.title, course_id:e.course_id, duration:e.duration }); setQuestions((e.questions||[]).map(q=>({ question:q.question, options:q.options||JSON.parse(q.options||"[]"), correct:q.correct_index??q.correct }))); setModal(true); }}>✏️</button>
                  <button style={S.btn("purple","sm")} onClick={()=>{ setViewRes(e); loadResults(e.id); }}>📊</button>
                  <button style={S.btn("danger","sm")} onClick={()=>del(e.id)}>🗑️</button>
                </div>
              </div>
            </GlassCard>
          );
        })}
        {exams.length===0 && <Empty icon="📋" text="No exams yet" />}
      </div>
      <Modal open={!!viewRes} onClose={()=>setViewRes(null)} title={`Results — ${viewRes?.title}`} w={500}>
        {results.length===0 ? <div style={{ textAlign:"center", color:"#475569", padding:24 }}>No results yet</div>
          : results.map(r=>(
            <div key={r.id} style={{ padding:"13px 0", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontWeight:700, color:C.white }}>{r.student_name}</div>
                <div style={{ fontSize:12, color:"#475569" }}>{r.student_id}</div>
                {r.score<60 && <div style={{ fontSize:11, color:"#F59E0B" }}>⚡ Boosted from {r.score}%</div>}
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:26, fontWeight:900, color:"#10B981" }}>{r.final_score}%</div>
                <Badge type="success">PASSED ✓</Badge>
              </div>
            </div>
          ))
        }
      </Modal>
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit Exam":"Create Exam"} w={640}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveExam}>Save</button></>}>
        <Inp label="Title *" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <Sel label="Course *" value={form.course_id} onChange={e=>setForm({...form,course_id:e.target.value})}>
            <option value="">Select</option>
            {courses.map(c=><option key={c.id} value={c.id}>{c.name} ({c.level})</option>)}
          </Sel>
          <Inp label="Duration (min)" type="number" value={form.duration} onChange={e=>setForm({...form,duration:parseInt(e.target.value)||60})} />
        </div>
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
            <strong style={{ color:C.white }}>Questions ({questions.length})</strong>
            <button style={S.btn("success","sm")} onClick={()=>{ setEditQIdx(null); setQForm({ question:"", options:["","","",""], correct:0 }); setQModal(true); }}>+ Add Q</button>
          </div>
          {questions.map((q,i)=>(
            <div key={i} style={{ padding:"9px 14px", background:"rgba(255,255,255,0.04)", borderRadius:8, marginBottom:7, display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, color:"#CBD5E1" }}>Q{i+1}: {q.question.substring(0,50)}...</span>
              <div style={{ display:"flex", gap:6 }}>
                <button style={S.btn("glass","sm")} onClick={()=>{ setEditQIdx(i); setQForm({ question:q.question, options:[...q.options], correct:q.correct }); setQModal(true); }}>✏️</button>
                <button style={S.btn("danger","sm")} onClick={()=>setQuestions(questions.filter((_,j)=>j!==i))}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      </Modal>
      <Modal open={qModal} onClose={()=>setQModal(false)} title={editQIdx!==null?"Edit Q":"Add Question"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setQModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={saveQ}>Save</button></>}>
        <Txtarea label="Question *" rows={2} value={qForm.question} onChange={e=>setQForm({...qForm,question:e.target.value})} />
        <div style={S.formGroup}>
          <label style={S.label}>Options (radio = correct)</label>
          {qForm.options.map((opt,i)=>(
            <div key={i} style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8 }}>
              <input type="radio" checked={qForm.correct===i} onChange={()=>setQForm({...qForm,correct:i})} style={{ width:16, height:16, cursor:"pointer" }} />
              <input style={{ ...S.input, flex:1 }} placeholder={`Option ${i+1}`} value={opt} onChange={e=>{ const opts=[...qForm.options]; opts[i]=e.target.value; setQForm({...qForm,options:opts}); }} />
              {qForm.correct===i && <Badge type="success">✓</Badge>}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

// ── GRADES ──────────────────────────────────────────────────────────────────
export function AdminGrades() {
  const [students, setStudents] = useState([]);
  const [sel, setSel] = useState(null);
  const [assignSubs, setAssignSubs] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    const load = async () => {
      try { const s = await usersAPI.getStudents(); setStudents(s.data||[]); } catch(e) { console.error(e); }
    };
    load();
  }, []);

  const loadReport = async (student) => {
    setSel(student);
    try {
      const [subs, results, assigns] = await Promise.all([
        assessmentAPI.mySubmissions ? fetch(`${process.env.REACT_APP_API_URL}/assessment/submissions/my`, { headers: { Authorization: `Bearer ${localStorage.getItem("uni_token")}` } }).then(r=>r.json()) : Promise.resolve({ data:[] }),
        assessmentAPI.getResults ? Promise.resolve({ data:[] }) : Promise.resolve({ data:[] }),
        assessmentAPI.getAssignments(),
      ]);
      setAssignSubs(subs.data||[]); setAssignments(assigns.data||[]);
    } catch(e) { console.error(e); }
  };

  const avgA = assignSubs.filter(s=>s.marks!=null).length > 0 ? Math.round(assignSubs.filter(s=>s.marks!=null).reduce((a,s)=>a+s.marks,0)/assignSubs.filter(s=>s.marks!=null).length) : null;
  const overall = avgA;
  const grade = calcGrade(overall);

  return (
    <div>
      <PageHdr title="Grades & Reports" />
      <div style={{ display:"grid", gridTemplateColumns:"260px 1fr", gap:20 }}>
        <div style={S.card}>
          <div style={{ padding:"14px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)", fontWeight:700, color:C.white }}>Students</div>
          {students.map(s=>(
            <div key={s.id} onClick={()=>loadReport(s)} style={{ padding:"12px 18px", borderBottom:"1px solid rgba(255,255,255,0.03)", cursor:"pointer", background:sel?.id===s.id?"rgba(59,130,246,0.1)":"transparent", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:C.white }}>{s.name}</div>
                <div style={{ fontSize:11, color:"#475569" }}>{s.student_id}</div>
              </div>
            </div>
          ))}
          {students.length===0 && <div style={{ padding:24, textAlign:"center", color:"#475569", fontSize:13 }}>No students</div>}
        </div>
        <div>
          {sel ? (
            <div>
              <div style={{ background:G.blue, borderRadius:14, padding:"22px 28px", marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:20, fontWeight:800, color:C.white }}>{sel.name}</div>
                  <div style={{ color:"rgba(255,255,255,0.6)", fontSize:13, marginTop:3 }}>{sel.student_id}</div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:58, fontWeight:900, color:C.white, lineHeight:1 }}>{grade}</div>
                  {overall!=null && <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>{overall}%</div>}
                </div>
              </div>
              <div style={S.card}>
                <div style={{ padding:"13px 18px", borderBottom:"1px solid rgba(255,255,255,0.06)", fontWeight:700, color:C.white }}>📝 Assignment Grades</div>
                <table style={S.table}>
                  <thead><tr><th style={S.th}>Assignment</th><th style={S.th}>Marks</th><th style={S.th}>Status</th></tr></thead>
                  <tbody>
                    {assignments.map(a=>{
                      const sub = assignSubs.find(s=>s.assignment_id===a.id);
                      return <tr key={a.id}><td style={{ ...S.td, fontSize:12 }}>{a.title}</td><td style={S.td}>{sub?.marks!=null?<strong style={{ color:"#10B981" }}>{sub.marks}%</strong>:"—"}</td><td style={S.td}><Badge type={sub?(sub.status==="graded"?"success":"warning"):"gray"}>{sub?sub.status:"—"}</Badge></td></tr>;
                    })}
                    {assignments.length===0 && <tr><td colSpan={3} style={{ ...S.td, textAlign:"center", color:"#475569" }}>No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          ) : <Empty icon="📊" text="Select a student" sub="View detailed report" />}
        </div>
      </div>
    </div>
  );
}

// ── ANNOUNCEMENTS ──────────────────────────────────────────────────────────
export function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title:"", message:"", target:"all" });

  const load = async () => {
    try {
      const [a, c] = await Promise.all([commAPI.getAnnouncements(), academicAPI.getCourses()]);
      setAnnouncements(a.data||[]); setCourses(c.data||[]);
    } catch(e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.message) return;
    try {
      if (editItem) await commAPI.updateAnnouncement(editItem.id, form);
      else await commAPI.createAnnouncement(form);
      setModal(false); setEditItem(null); load();
    } catch(e) { alert(e.message); }
  };
  const del = async (id) => { if(!window.confirm("Delete?")) return; try { await commAPI.deleteAnnouncement(id); load(); } catch(e) { alert(e.message); } };

  return (
    <div>
      <PageHdr title="Announcements" action={<button style={S.btn("primary")} onClick={()=>{ setEditItem(null); setForm({ title:"", message:"", target:"all" }); setModal(true); }}>➕ New</button>} />
      {announcements.map(a=>(
        <div key={a.id} style={{ ...S.card, marginBottom:14, borderLeft:"3px solid #3B82F6" }}>
          <div style={{ padding:"18px 22px", display:"flex", justifyContent:"space-between" }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:8 }}>
                <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:C.white }}>{a.title}</h3>
                <Badge type="info">{a.target==="all"?"All Students":a.target}</Badge>
              </div>
              <p style={{ margin:"0 0 8px", fontSize:14, color:"#94A3B8", lineHeight:1.7 }}>{a.message}</p>
              <div style={{ fontSize:12, color:"#475569" }}>{new Date(a.created_at).toLocaleDateString("en-PK")}</div>
            </div>
            <div style={{ display:"flex", gap:8, marginLeft:16 }}>
              <button style={S.btn("glass","sm")} onClick={()=>{ setEditItem(a); setForm({ title:a.title, message:a.message, target:a.target }); setModal(true); }}>✏️</button>
              <button style={S.btn("danger","sm")} onClick={()=>del(a.id)}>🗑️</button>
            </div>
          </div>
        </div>
      ))}
      {announcements.length===0 && <Empty icon="📢" text="No announcements" />}
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?"Edit":"New Announcement"}
        footer={<><button style={S.btn("ghost")} onClick={()=>setModal(false)}>Cancel</button><button style={S.btn("primary")} onClick={save}>Post</button></>}>
        <Inp label="Title *" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} />
        <Txtarea label="Message *" rows={4} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} />
        <Sel label="Target" value={form.target} onChange={e=>setForm({...form,target:e.target.value})}>
          <option value="all">All Students</option>
          {courses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </Sel>
      </Modal>
    </div>
  );
}